/**
 * MediaImageManager – canonical image upload component for all entity types.
 * Single source of truth for cover + gallery image management.
 *
 * Handles:
 * - Combined cover + gallery grid with badge
 * - Auto-first-as-cover logic
 * - Plan-based X/MAX display
 * - Limit enforcement with inline alert
 * - Draft and finalized asset display
 * - Optimistic add/remove
 * - Set cover from gallery (atomic swap)
 * - Drag & reorder gallery (persisted sort_order)
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { ImagePlus, X, AlertCircle, GripVertical, MoreVertical, Star, Check, Upload, Pencil } from 'lucide-react';
import { getImageQuality } from '@/lib/media/imageQuality';
import { type ImageIntent, defaultImageIntent } from '@/lib/media/imageIntent';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { isSupportPreviewActive } from '@/lib/supportPreview';
import {
  generateUploadRequestId,
  trackPickerOpened,
  trackFileSelected,
  trackFileDialogCanceled,
  trackMediaUploadCtaClicked,
  detectPlatform,
} from '@/lib/media/uploadTelemetry';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { EXPLORE_DEBUG } from '@/lib/debug';
import { captureAppError } from '@/lib/monitoring/sentry';
import {
  resolveAssetUrl,
  resolveAssetUrlAsync,
  deleteAsset,
  listAssetsByDraftSession,
  setCoverAsset,
  reorderGalleryAssets,
} from '@/lib/media/mediaApi';
import type { MediaAsset, OwnerType } from '@/lib/media/mediaTypes';
import { useMediaAssets } from '@/hooks/useMediaAssets';
import { useEntityMediaLimit } from '@/hooks/useEntityMediaLimit';
import { assessImageSuitability } from '@/lib/media/assessImageSuitability';
import { ImageEditorDialog, type EditorMode } from '@/components/media/ImageEditorDialog';
import { PendingUploadCard } from '@/components/media/PendingUploadCard';
import { MediaQualityHint } from '@/components/media/MediaQualityHint';

/* ── Types ── */

interface PendingUpload {
  id: string;
  previewUrl: string;
}

export interface UploadHook {
  isUploading: boolean;
  progress: number;
  errors: string[];
  uploadFiles: (files: File[]) => Promise<MediaAsset[]>;
  cancel: () => void;
}

interface MediaImageManagerProps {
  ownerType: OwnerType;
  ownerId?: string | null;
  producerId?: string;
  draftSessionId: string;
  coverUpload: UploadHook;
  galleryUpload: UploadHook;
  disabled?: boolean;
  helperText?: string;
  className?: string;
  /** When set, fetches assets with this season_key instead of NULL (base). */
  seasonKey?: string | null;
  /** Override max gallery count (e.g. 4 for season mode). */
  maxGalleryOverride?: number;
  /** Called when cover asset changes (for overlay sync). */
  onCoverChange?: (assetId: string | null) => void;
  /** Called when gallery asset list changes (for overlay sync). */
  onGalleryChange?: (assetIds: string[]) => void;
  /** Optional label override, e.g. "Säsong: Vinter" */
  sectionLabel?: string;
  /** Image intent for context-aware quality rules. Auto-derived from ownerType if omitted. */
  imageIntent?: ImageIntent;
}

/* ── Quality badge ── */

function ImageQualityBadge({ width, intent }: { width: number | null; intent: ImageIntent }) {
  if (width == null) return null;
  const { quality } = getImageQuality(width, intent);
  if (quality === 'good') return null; // No badge needed for good images

  const dotColor = quality === 'ok' ? 'bg-amber-400' : 'bg-red-400';
  return (
    <span
      className="absolute bottom-1 right-1 rounded-full h-2 w-2 pointer-events-auto"
      title={quality === 'ok' ? 'Kan förbättras' : 'För liten bild'}
    >
      <span className={cn('block h-2 w-2 rounded-full', dotColor)} />
    </span>
  );
}

/* ── Sortable thumbnail ── */

function SortableGalleryItem({
  asset,
  url,
  index,
  disabled,
  isUploading,
  isCoverSuitable,
  onDelete,
  onSetCover,
  onEdit,
  qualityIntent,
}: {
  asset: MediaAsset;
  url: string | undefined;
  index: number;
  disabled: boolean;
  isUploading: boolean;
  isCoverSuitable: boolean;
  onDelete: (a: MediaAsset) => void;
  onSetCover: (a: MediaAsset) => void;
  onEdit: (a: MediaAsset) => void;
  qualityIntent: ImageIntent;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: asset.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square rounded-lg overflow-hidden bg-muted group"
    >
      {url ? (
        <img
          src={url}
          alt={asset.alt_text || `Bild ${index + 1}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImagePlus className="h-5 w-5 text-muted-foreground/40" />
        </div>
      )}

      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className={cn(
          'absolute bottom-1 left-1 rounded p-0.5',
          'bg-background/80 backdrop-blur-sm text-muted-foreground',
          'opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity',
          'cursor-grab active:cursor-grabbing touch-none',
        )}
        aria-label="Dra för att ändra ordning"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      {/* Quality badge */}
      <ImageQualityBadge width={asset.width} intent={qualityIntent} />

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />

      {/* Actions menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled || isUploading}
            className={cn(
              'absolute top-1 right-1 rounded-full p-1',
              'bg-background/80 backdrop-blur-sm text-muted-foreground',
              'opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity',
              'hover:text-foreground hover:bg-background',
              'focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
            aria-label="Bildåtgärder"
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[160px]">
          <DropdownMenuItem onClick={() => onEdit(asset)}>
            <Pencil className="h-4 w-4 mr-2" />
            Redigera bild
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSetCover(asset)}>
            <Star className="h-4 w-4 mr-2" />
            Sätt som omslag
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onDelete(asset)}
            className="text-destructive focus:text-destructive"
          >
            <X className="h-4 w-4 mr-2" />
            Ta bort
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/* ── Cover thumbnail (not draggable) ── */

function CoverItem({
  asset,
  url,
  disabled,
  isUploading,
  onDelete,
  onEdit,
  qualityIntent,
}: {
  asset: MediaAsset;
  url: string | undefined;
  disabled: boolean;
  isUploading: boolean;
  onDelete: (a: MediaAsset) => void;
  onEdit: (a: MediaAsset) => void;
  qualityIntent: ImageIntent;
}) {
  return (
    <div data-testid="event-media-cover-item" className="relative aspect-square rounded-lg overflow-hidden bg-muted group">
      {url ? (
        <img
          src={url}
          alt={asset.alt_text || 'Omslagsbild'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImagePlus className="h-5 w-5 text-muted-foreground/40" />
        </div>
      )}

      {/* Cover badge */}
      <Badge
        variant="secondary"
        className="absolute top-1 left-1 text-[10px] px-1.5 py-0 bg-background/80 backdrop-blur-sm"
      >
        Omslag
      </Badge>

      {/* Quality badge */}
      <ImageQualityBadge width={asset.width} intent={qualityIntent} />

      {/* Overlay – click to edit */}
      <button
        type="button"
        onClick={() => onEdit(asset)}
        disabled={disabled || isUploading}
        className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors cursor-pointer"
        aria-label="Redigera omslagsbild"
      >
        <span className={cn(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'rounded-full p-2 bg-background/80 backdrop-blur-sm text-muted-foreground',
          'opacity-0 group-hover:opacity-100 transition-opacity',
        )}>
          <Pencil className="h-4 w-4" />
        </span>
      </button>

      {/* Delete button */}
      <button
        type="button"
        onClick={() => onDelete(asset)}
        disabled={disabled || isUploading}
        data-testid="event-media-delete-cover"
        className={cn(
          'absolute top-1 right-1 rounded-full p-1',
          'bg-background/80 backdrop-blur-sm text-muted-foreground',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          'hover:text-destructive hover:bg-background',
          'focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
        aria-label="Ta bort omslagsbild"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ── Main Component ── */

export function MediaImageManager({
  ownerType,
  ownerId,
  producerId,
  draftSessionId,
  coverUpload,
  galleryUpload,
  disabled = false,
  helperText = 'Valfritt · Lägg till bilder. Bilder sparas automatiskt.',
  className,
  seasonKey = null,
  maxGalleryOverride,
  onCoverChange,
  onGalleryChange,
  sectionLabel,
  imageIntent,
}: MediaImageManagerProps) {
  // Derive intent from ownerType if not explicitly provided
  const coverIntent: ImageIntent = imageIntent ?? defaultImageIntent(ownerType, 'cover');
  const galleryIntent: ImageIntent = imageIntent === 'thumbnail' ? 'thumbnail' : 'gallery';
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fallbackInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useRef(`mim-input-${crypto.randomUUID().slice(0, 8)}`).current;

  /**
   * Dialog session ref – ensures exactly one outcome event per picker open.
   * Prevents double-toast and double-tracking on Samsung/Android.
   */
  const dialogRef = useRef<{
    request_id: string | null;
    resolved: boolean;
  }>({ request_id: null, resolved: true });
  const cancelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const pickerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Image Editor state ──
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorImageSrc, setEditorImageSrc] = useState<string>('');
  const [editorFileName, setEditorFileName] = useState<string>('cover.jpg');
  const [editorSaving, setEditorSaving] = useState(false);
  /** Pending gallery uploads – shown as skeleton cards until resolved */
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  /** What triggered the editor: 'upload' (new file), 'gallery' (set-as-cover), 'edit-cover', 'edit-gallery' */
  const editorModeRef = useRef<'upload' | 'gallery' | 'edit-cover' | 'edit-gallery'>('upload');
  /** Dialog mode for ImageEditorDialog: 'cover' (16:9) or 'gallery' (free crop) */
  const [editorDialogMode, setEditorDialogMode] = useState<EditorMode>('cover');
  /** When mode=gallery, the asset being promoted */
  const editorGalleryAssetRef = useRef<MediaAsset | null>(null);
  /** Remaining gallery files to upload after editor save */
  const pendingGalleryFilesRef = useRef<File[]>([]);
  /** Original file before editor crop (used for gallery fallback) */
  const pendingOriginalFileRef = useRef<File | null>(null);

  const telemetrySurface = ownerType === 'producer' ? 'producer_profile'
    : ownerType === 'producer_shop' ? 'shop_editor'
    : ownerType === 'product' ? 'product_editor'
    : ownerType === 'event' ? 'event_edit'
    : ownerType === 'place' ? 'place_editor'
    : 'media_uploader';

  const isSeasonMode = seasonKey !== null && seasonKey !== undefined;

  const effectiveProducerId = (ownerType === 'producer' || ownerType === 'producer_shop') ? (ownerId ?? producerId) ?? undefined : producerId;
  const { data: mediaLimit } = useEntityMediaLimit(ownerType, ownerId, effectiveProducerId);

  // Fetch existing finalized assets – filtered by seasonKey (null = base, string = seasonal)
  const seasonFilter = isSeasonMode ? seasonKey : null;
  const { data: ownerCover = [] } = useMediaAssets(ownerType, ownerId, 'cover', seasonFilter);
  const { data: ownerGallery = [] } = useMediaAssets(ownerType, ownerId, 'gallery', seasonFilter);

  // Fetch draft assets (only when no ownerId yet)
  const { data: draftAssets = [] } = useQuery<MediaAsset[]>({
    queryKey: ['media-assets-draft', draftSessionId],
    queryFn: () => listAssetsByDraftSession(draftSessionId),
    enabled: !ownerId && !!draftSessionId,
  });

  const draftCover = draftAssets.filter(a => a.kind === 'cover');
  const draftGallery = draftAssets.filter(a => a.kind === 'gallery');

  if (EXPLORE_DEBUG && (draftAssets.length > 0 || ownerCover.length > 0 || ownerGallery.length > 0)) {
    console.debug('[MediaImageManager] assets loaded', {
      ownerId,
      draftSessionId,
      ownerCover: ownerCover.length,
      ownerGallery: ownerGallery.length,
      draftCover: draftCover.length,
      draftGallery: draftGallery.length,
    });
  }

  const baseCover = ownerId ? ownerCover : draftCover;
  const baseGallery = ownerId ? ownerGallery : draftGallery;

  // Stable identity keys to avoid re-render loops when array refs change but content is same
  const baseCoverKey = useMemo(() => baseCover.map(a => a.id).join(','), [baseCover]);
  const baseGalleryKey = useMemo(() => baseGallery.map(a => a.id).join(','), [baseGallery]);

  // Local state for optimistic updates
  const [localCover, setLocalCover] = useState<MediaAsset[]>(baseCover);
  const [localGallery, setLocalGallery] = useState<MediaAsset[]>(baseGallery);

  // Sync server → local only when the actual asset IDs change (not on every render)
  const prevCoverKeyRef = useRef(baseCoverKey);
  const prevGalleryKeyRef = useRef(baseGalleryKey);

  useEffect(() => {
    if (prevCoverKeyRef.current !== baseCoverKey) {
      prevCoverKeyRef.current = baseCoverKey;
      setLocalCover(baseCover);
    }
  }, [baseCoverKey, baseCover]);

  useEffect(() => {
    if (prevGalleryKeyRef.current !== baseGalleryKey) {
      prevGalleryKeyRef.current = baseGalleryKey;
      setLocalGallery(baseGallery);
    }
  }, [baseGalleryKey, baseGallery]);

  // ── Normalize: exactly one active cover ──
  // If multiple cover assets exist (stale DB state), pick the newest and treat the rest as gallery.
  const activeCover = useMemo<MediaAsset | null>(() => {
    if (localCover.length === 0) return null;
    // Pick the most recently created cover asset
    const sorted = [...localCover].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return sorted[0];
  }, [localCover]);

  // Stale covers (extras beyond the active one) are shown in the gallery instead
  const staleCoverAsGallery = useMemo<MediaAsset[]>(() => {
    if (!activeCover || localCover.length <= 1) return [];
    return localCover
      .filter(a => a.id !== activeCover.id)
      .map(a => ({ ...a, kind: 'gallery' as const }));
  }, [localCover, activeCover]);

  const effectiveGallery = useMemo(
    () => [...staleCoverAsGallery, ...localGallery],
    [staleCoverAsGallery, localGallery],
  );

  const allAssets = useMemo(() => [...localCover, ...localGallery], [localCover, localGallery]);
  const allAssetsKey = useMemo(() => allAssets.map(a => a.id).join(','), [allAssets]);
  const hasCover = activeCover !== null;
  const totalCount = allAssets.length + pendingUploads.length;
  const effectiveMaxGallery = maxGalleryOverride ?? Math.max(0, mediaLimit.maxImages - 1);
  const galleryMax = effectiveMaxGallery;
  const effectiveMaxImages = maxGalleryOverride ? maxGalleryOverride + 1 : mediaLimit.maxImages;
  const galleryAtLimit = effectiveGallery.length >= galleryMax || (!maxGalleryOverride && mediaLimit.isAtLimit);
  const isUploading = coverUpload.isUploading || galleryUpload.isUploading;

  // Resolve URLs for private-media assets
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (allAssets.length === 0) return;
    let cancelled = false;
    const resolve = async () => {
      if (EXPLORE_DEBUG) {
        console.debug('[MediaImageManager] resolving URLs for', allAssets.length, 'assets');
      }

      const entries: Record<string, string> = {};
      for (const asset of allAssets) {
        try {
          const syncUrl = resolveAssetUrl(asset, 320);
          if (syncUrl) {
            entries[asset.id] = syncUrl;
          } else {
            const asyncUrl = await resolveAssetUrlAsync(asset, 320);
            if (asyncUrl) {
              entries[asset.id] = asyncUrl;
            } else if (EXPLORE_DEBUG) {
              console.warn('[MediaImageManager] no URL resolved for asset', asset.id, { bucket: asset.bucket, path_320: asset.path_320 });
            }
          }
        } catch (err: any) {
          console.error('[MediaImageManager] URL resolve failed for asset', asset.id, err);
          captureAppError(err, {
            feature: 'media_image_manager',
            action: 'resolve_url',
            asset_id: asset.id,
            bucket: asset.bucket,
          });
          toast.error('Kunde inte visa miniatyrbild');
        }
      }
      if (!cancelled) {
        setResolvedUrls(prev => ({ ...prev, ...entries }));
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [allAssetsKey]); // eslint-disable-line react-hooks/exhaustive-deps -- keyed by stable string

  const invalidateQueries = useCallback(() => {
    if (ownerId) {
      queryClient.invalidateQueries({ queryKey: ['media-assets', ownerType, ownerId] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['media-assets-draft', draftSessionId] });
    }
    queryClient.invalidateQueries({ queryKey: ['entity-media-limit', ownerType, ownerId] });
  }, [ownerId, ownerType, draftSessionId, queryClient]);

  const handleFilesSelected = useCallback(async (files: File[]) => {
    if (disabled || isUploading || files.length === 0 || isSupportPreviewActive()) return;

    const available = effectiveMaxImages - totalCount;
    if (available <= 0) {
      toast.info(`Du har nått bildgränsen (${effectiveMaxImages}).`);
      return;
    }

    const filesToUse = files.slice(0, available);
    if (files.length > available) {
      toast.info(`Max ${effectiveMaxImages} bilder – ${files.length - available} hoppades över`);
    }

    if (!hasCover) {
      const [coverFile, ...rest] = filesToUse;

      // Open editor for the cover image before upload
      const objectUrl = URL.createObjectURL(coverFile);
      editorModeRef.current = 'upload';
      setEditorDialogMode('cover');
      editorGalleryAssetRef.current = null;
      pendingOriginalFileRef.current = coverFile;
      // Store remaining gallery files to upload after editor save
      pendingGalleryFilesRef.current = rest;
      setEditorImageSrc(objectUrl);
      setEditorFileName(coverFile.name);
      setEditorOpen(true);
    } else {
      const galleryResult = await galleryUpload.uploadFiles(filesToUse);
      setLocalGallery(prev => {
        const next = [...prev, ...galleryResult];
        onGalleryChange?.(next.map(a => a.id));
        return next;
      });
    }

    invalidateQueries();
  }, [disabled, isUploading, hasCover, totalCount, effectiveMaxImages, coverUpload, galleryUpload, invalidateQueries, onCoverChange, onGalleryChange]);

  const handleDelete = useCallback(async (asset: MediaAsset) => {
    if (isSupportPreviewActive()) return;
    // staleCoverAsGallery items have their kind remapped to 'gallery' for display,
    // but they live in localCover (not localGallery). Check by id instead of kind
    // so the correct state slice is updated optimistically.
    const isInCover = localCover.some(a => a.id === asset.id);
    try {
      if (isInCover) {
        setLocalCover(prev => prev.filter(a => a.id !== asset.id));
        // Only clear the cover callback if we deleted the currently active cover.
        // Deleting a stale cover (staleCoverAsGallery) should not clear it.
        if (activeCover?.id === asset.id) onCoverChange?.(null);
      } else {
        setLocalGallery(prev => {
          const next = prev.filter(a => a.id !== asset.id);
          onGalleryChange?.(next.map(a => a.id));
          return next;
        });
      }
      await deleteAsset(asset.id);
      invalidateQueries();
    } catch (err) {
      toast.error('Kunde inte ta bort bilden');
      if (isInCover) {
        setLocalCover(prev => [...prev, asset]);
        if (activeCover?.id === asset.id) onCoverChange?.(asset.id);
      } else {
        setLocalGallery(prev => {
          const next = [...prev, asset];
          onGalleryChange?.(next.map(a => a.id));
          return next;
        });
      }
    }
  }, [localCover, activeCover, invalidateQueries, onCoverChange, onGalleryChange]);

  const handleSetCover = useCallback(async (asset: MediaAsset) => {
    if (asset.kind === 'cover' || isSupportPreviewActive()) return;

    // Open editor for the gallery image – always use the largest variant (1280)
    // Do NOT use resolvedUrls (those are 320px thumbnails)
    try {
      const url = resolveAssetUrl(asset, 1280)
        || await resolveAssetUrlAsync(asset, 1280);

      if (!url) {
        toast.error('Kunde inte ladda bilden för redigering.');
        return;
      }

      editorModeRef.current = 'gallery';
      setEditorDialogMode('cover');
      editorGalleryAssetRef.current = asset;
      pendingGalleryFilesRef.current = [];
      setEditorImageSrc(url);
      setEditorFileName(asset.alt_text || 'gallery-image.jpg');
      setEditorOpen(true);
    } catch (err) {
      console.error('[MediaImageManager] failed to open editor for gallery→cover', err);
      toast.error('Kunde inte öppna bildredigeraren.');
    }
  }, [resolvedUrls]);

  // ── Edit existing image handler ──
  const handleEditExisting = useCallback(async (asset: MediaAsset, kind: 'cover' | 'gallery') => {
    if (isSupportPreviewActive()) return;
    try {
      const url = resolveAssetUrl(asset, 1280)
        || await resolveAssetUrlAsync(asset, 1280);

      if (!url) {
        toast.error('Kunde inte ladda bilden för redigering.');
        return;
      }

      editorModeRef.current = kind === 'cover' ? 'edit-cover' : 'edit-gallery';
      editorGalleryAssetRef.current = asset;
      pendingGalleryFilesRef.current = [];
      setEditorDialogMode(kind === 'cover' ? 'cover' : 'gallery');
      setEditorImageSrc(url);
      setEditorFileName(asset.alt_text || `${kind}-image.jpg`);
      setEditorOpen(true);
    } catch (err) {
      console.error('[MediaImageManager] failed to open editor for existing image', err);
      toast.error('Kunde inte öppna bildredigeraren.');
    }
  }, []);

  const handleEditCover = useCallback((asset: MediaAsset) => handleEditExisting(asset, 'cover'), [handleEditExisting]);
  const handleEditGallery = useCallback((asset: MediaAsset) => handleEditExisting(asset, 'gallery'), [handleEditExisting]);

  // ── Editor save handler ──
  const handleEditorSave = useCallback(async (editedFile: File) => {
    setEditorSaving(true);
    try {
      const mode = editorModeRef.current;

      if (mode === 'upload') {
        // New cover upload
        const coverResult = await coverUpload.uploadFiles([editedFile]);
        if (coverResult.length > 0) {
          setLocalCover(coverResult);
          onCoverChange?.(coverResult[0].id);
        }
        // Upload remaining gallery files
        const rest = pendingGalleryFilesRef.current;
        if (rest.length > 0) {
          const galleryResult = await galleryUpload.uploadFiles(rest);
          setLocalGallery(prev => {
            const next = [...prev, ...galleryResult];
            onGalleryChange?.(next.map(a => a.id));
            return next;
          });
        }
        pendingGalleryFilesRef.current = [];
      } else if (mode === 'gallery') {
        // Gallery → cover: upload edited file as new cover, remove old gallery asset
        const asset = editorGalleryAssetRef.current;
        const coverResult = await coverUpload.uploadFiles([editedFile]);
        if (coverResult.length > 0) {
          const oldCover = localCover[0];
          setLocalCover(coverResult);
          onCoverChange?.(coverResult[0].id);
          if (oldCover) {
            setLocalGallery(prev => {
              const next = [...prev, { ...oldCover, kind: 'gallery' as const }];
              onGalleryChange?.(next.map(a => a.id));
              return next;
            });
          }
          if (asset) {
            setLocalGallery(prev => {
              const next = prev.filter(a => a.id !== asset.id);
              onGalleryChange?.(next.map(a => a.id));
              return next;
            });
            try { await deleteAsset(asset.id); } catch { /* best effort */ }
          }
        }
        editorGalleryAssetRef.current = null;
      } else if (mode === 'edit-cover') {
        // Re-edit existing cover: upload edited as new cover, delete old
        const oldAsset = editorGalleryAssetRef.current;
        const coverResult = await coverUpload.uploadFiles([editedFile]);
        if (coverResult.length > 0) {
          setLocalCover(coverResult);
          onCoverChange?.(coverResult[0].id);
        }
        if (oldAsset) {
          try { await deleteAsset(oldAsset.id); } catch { /* best effort */ }
        }
        editorGalleryAssetRef.current = null;
      } else if (mode === 'edit-gallery') {
        // Re-edit existing gallery image: upload edited, remove old
        const oldAsset = editorGalleryAssetRef.current;
        const galleryResult = await galleryUpload.uploadFiles([editedFile]);
        if (galleryResult.length > 0) {
          setLocalGallery(prev => {
            // Replace old asset with new at same position
            const idx = oldAsset ? prev.findIndex(a => a.id === oldAsset.id) : -1;
            if (idx >= 0) {
              const next = [...prev];
              next.splice(idx, 1, ...galleryResult);
              onGalleryChange?.(next.map(a => a.id));
              return next;
            }
            const next = [...prev, ...galleryResult];
            onGalleryChange?.(next.map(a => a.id));
            return next;
          });
        }
        if (oldAsset) {
          try { await deleteAsset(oldAsset.id); } catch { /* best effort */ }
        }
        editorGalleryAssetRef.current = null;
      }

      invalidateQueries();
      setEditorOpen(false);
      toast.success(mode.startsWith('edit') ? 'Bilden har uppdaterats' : 'Omslagsbild sparad');
    } catch (err) {
      console.error('[MediaImageManager] editor save failed', err);
      toast.error('Kunde inte spara omslagsbilden.');
    } finally {
      setEditorSaving(false);
    }
  }, [coverUpload, galleryUpload, localCover, invalidateQueries, onCoverChange, onGalleryChange]);

  // ── Editor gallery fallback handler ──
  const handleEditorSaveAsGallery = useCallback(async () => {
    setEditorSaving(true);
    try {
      if (editorModeRef.current === 'gallery') {
        toast.info('Bilden finns redan i galleriet.');
        setEditorOpen(false);
        editorGalleryAssetRef.current = null;
        return;
      }

      const originalFile = pendingOriginalFileRef.current;
      if (!originalFile) {
        toast.error('Originalfilen kunde inte hittas.');
        return;
      }

      // ── Immediately show pending placeholder ──
      const tempId = crypto.randomUUID();
      const previewUrl = URL.createObjectURL(originalFile);
      setPendingUploads(prev => [...prev, { id: tempId, previewUrl }]);

      // Close editor right away so user sees the gallery with placeholder
      setEditorOpen(false);

      console.log('[MediaImageManager] gallery fallback using original file', {
        name: originalFile.name,
        size: originalFile.size,
        tempId,
      });

      const galleryResult = await galleryUpload.uploadFiles([originalFile]);

      // Remove pending placeholder
      setPendingUploads(prev => {
        const next = prev.filter(p => p.id !== tempId);
        // Revoke object URL to free memory
        URL.revokeObjectURL(previewUrl);
        return next;
      });

      if (galleryResult.length === 0) {
        console.warn('[MediaImageManager] gallery fallback upload returned no results');
        return;
      }

      setLocalGallery(prev => {
        const next = [...prev, ...galleryResult];
        onGalleryChange?.(next.map(a => a.id));
        return next;
      });

      // Also upload any pending gallery files from the original batch
      const rest = pendingGalleryFilesRef.current;
      if (rest.length > 0) {
        const restResult = await galleryUpload.uploadFiles(rest);
        if (restResult.length > 0) {
          setLocalGallery(prev => {
            const next = [...prev, ...restResult];
            onGalleryChange?.(next.map(a => a.id));
            return next;
          });
        }
      }

      pendingGalleryFilesRef.current = [];
      pendingOriginalFileRef.current = null;

      toast.success('Bilden har lagts till i galleriet.');
      invalidateQueries();
    } catch (err) {
      console.error('[MediaImageManager] gallery fallback save failed', err);
      toast.error('Kunde inte spara bilden.');
      // Clean up any remaining pending items
      setPendingUploads([]);
    } finally {
      setEditorSaving(false);
    }
  }, [galleryUpload, invalidateQueries, onGalleryChange]);

  // ── Drag & Drop ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  // handleDragEnd is defined as handleDragEndWithCallback below (after hasLimit)

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) handleFilesSelected(files);
  }, [handleFilesSelected]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear cancel timer — a file was selected
    if (cancelTimerRef.current) {
      clearTimeout(cancelTimerRef.current);
      cancelTimerRef.current = null;
    }
    if (pickerTimeoutRef.current) {
      clearTimeout(pickerTimeoutRef.current);
      pickerTimeoutRef.current = null;
    }

    const files = Array.from(e.target.files || []);
    const requestId = dialogRef.current.request_id ?? generateUploadRequestId();

    if (!dialogRef.current.resolved) {
      dialogRef.current.resolved = true;
      trackFileSelected(requestId, files, {
        ownerType, ownerId, draftSessionId, surface: telemetrySurface,
      }, track);
    }

    if (files.length === 0) {
      toast.error('Ingen bild valdes. Försök igen.');
      e.target.value = '';
      return;
    }

    handleFilesSelected(files);
    setShowFallback(false);
    e.target.value = '';
  }, [handleFilesSelected, ownerType, ownerId, draftSessionId, telemetrySurface]);

  /** Track CTA click + open dialog session for outcome tracking */
  const handleCtaClick = useCallback(() => {
    trackMediaUploadCtaClicked({
      surface: telemetrySurface,
      ownerType,
      ownerId,
      draftSessionId,
      producerId,
    }, track);

    // Start dialog session
    const requestId = generateUploadRequestId();
    dialogRef.current = { request_id: requestId, resolved: false };
    trackPickerOpened(requestId, {
      ownerType, ownerId, draftSessionId, surface: telemetrySurface,
    }, track);

    // Show fallback hint if picker never opens (2s timeout)
    pickerTimeoutRef.current = setTimeout(() => {
      pickerTimeoutRef.current = null;
      if (!dialogRef.current.resolved) {
        setShowFallback(true);
      }
    }, 2000);
  }, [ownerType, ownerId, draftSessionId, producerId, telemetrySurface]);

  /** Fallback: plain input for when dropzone/dialog doesn't open */
  const handleFallbackChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFilesSelected(files);
      setShowFallback(false);
    } else {
      toast.error('Ingen bild valdes. Försök igen.');
    }
    e.target.value = '';
  }, [handleFilesSelected]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (cancelTimerRef.current) clearTimeout(cancelTimerRef.current);
      if (pickerTimeoutRef.current) clearTimeout(pickerTimeoutRef.current);
    };
  }, []);

  const hasLimit = isSeasonMode ? true : mediaLimit.maxImages < 999;

  // Also notify callbacks when drag reorder happens
  const handleDragEndWithCallback = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localGallery.findIndex(a => a.id === active.id);
    const newIndex = localGallery.findIndex(a => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...localGallery];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    setLocalGallery(reordered);
    onGalleryChange?.(reordered.map(a => a.id));

    if (ownerId) {
      try {
        await reorderGalleryAssets(ownerType, ownerId, reordered.map(a => a.id));
      } catch (err) {
        toast.error('Kunde inte spara ordningen');
        setLocalGallery(localGallery);
        onGalleryChange?.(localGallery.map(a => a.id));
      }
    }
  }, [localGallery, ownerId, ownerType, onGalleryChange]);

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium">{sectionLabel ?? 'Bilder'}</p>
          <p className="text-xs text-muted-foreground truncate">{helperText}</p>
        </div>
        {hasLimit && (
          <span className="text-xs text-muted-foreground shrink-0 ml-2">
            {totalCount} / {effectiveMaxImages}
          </span>
        )}
      </div>

      {/* Limit reached alert */}
      {hasLimit && totalCount >= effectiveMaxImages && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-2.5">
          <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            Du har nått bildgränsen ({effectiveMaxImages}).{!isSeasonMode && ' Ta bort en bild eller uppgradera.'}
          </p>
        </div>
      )}

      {/* Samsung-safe: sr-only input with label (not display:none) */}
      <input
        ref={fileInputRef}
        id={fileInputId}
        type="file"
        data-testid="event-media-file-input"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        className="sr-only"
        onChange={handleInputChange}
        disabled={disabled || isUploading}
      />

      {/* Thumbnails grid + dropzone */}
      {totalCount === 0 ? (
        <label
          htmlFor={fileInputId}
          data-testid="event-media-empty-dropzone"
          onClick={handleCtaClick}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className={cn(
            'w-full rounded-xl border-2 border-dashed border-muted-foreground/25 p-8',
            'flex flex-col items-center justify-center gap-2',
            'text-muted-foreground hover:border-primary/40 hover:text-foreground',
            'transition-colors cursor-pointer',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            (disabled || isUploading) && 'opacity-50 pointer-events-none',
          )}
        >
          <ImagePlus className="h-8 w-8" />
          <span className="text-sm">Lägg till en bild</span>
        </label>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEndWithCallback}
          >
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="event-media-grid">
              {/* Cover – not draggable, always first, exactly one */}
              {activeCover && (
                <CoverItem
                  key={activeCover.id}
                  asset={activeCover}
                  url={resolvedUrls[activeCover.id]}
                  disabled={disabled}
                  isUploading={isUploading}
                  onDelete={handleDelete}
                  onEdit={handleEditCover}
                  qualityIntent={coverIntent}
                />
              )}

              {/* Gallery – sortable */}
              <SortableContext
                items={effectiveGallery.map(a => a.id)}
                strategy={rectSortingStrategy}
              >
                {effectiveGallery.map((asset, idx) => {
                  const coverOk = asset.width && asset.height
                    ? assessImageSuitability(asset.width, asset.height, coverIntent).suitability === 'ok'
                    : false;
                  return (
                    <SortableGalleryItem
                      key={asset.id}
                      asset={asset}
                      url={resolvedUrls[asset.id]}
                      index={idx}
                      disabled={disabled}
                      isUploading={isUploading}
                      isCoverSuitable={coverOk}
                      onDelete={handleDelete}
                      onSetCover={handleSetCover}
                      onEdit={handleEditGallery}
                      qualityIntent={galleryIntent}
                    />
                  );
                })}
              </SortableContext>

              {/* Pending upload placeholders */}
              {pendingUploads.map(p => (
                <PendingUploadCard key={p.id} previewUrl={p.previewUrl} />
              ))}
              {!galleryAtLimit && totalCount < effectiveMaxImages && (
                <label
                  htmlFor={fileInputId}
                  data-testid="event-media-add-button"
                  onClick={handleCtaClick}
                  className={cn(
                    'aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25',
                    'flex items-center justify-center',
                    'text-muted-foreground hover:border-primary/40 hover:text-foreground',
                    'transition-colors cursor-pointer',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    (disabled || isUploading) && 'opacity-50 pointer-events-none',
                  )}
                  aria-label="Lägg till fler bilder"
                >
                  <ImagePlus className="h-5 w-5" />
                </label>
              )}
            </div>
          </DndContext>
        </div>
      )}

      {/* Shared quality hint – calm summary with expandable explanation */}
      <MediaQualityHint coverAsset={activeCover} intent={coverIntent} />

      {/* Upload progress */}
      {isUploading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="h-1 flex-1 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300 rounded-full"
              style={{ width: `${Math.max(coverUpload.progress, galleryUpload.progress)}%` }}
            />
          </div>
          <span>Laddar upp…</span>
        </div>
      )}

      {/* Fallback UI for Samsung/mobile when picker doesn't open */}
      {showFallback && (
        <div className="rounded-lg border border-border bg-muted/50 p-3 space-y-2">
          <p className="text-xs text-muted-foreground">
            Problem att öppna bildväljaren? Prova knappen nedan, eller öppna i Chrome.
          </p>
          <label className="inline-flex items-center gap-1.5 text-xs font-medium text-primary cursor-pointer hover:underline">
            <Upload className="h-3.5 w-3.5" />
            Välj bild härifrån
            <input
              ref={fallbackInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif"
              multiple
              className="sr-only"
              onChange={handleFallbackChange}
              disabled={disabled || isUploading}
            />
          </label>
        </div>
      )}

      {/* Image editor dialog – reused for upload, set-as-cover, and re-edit */}
      <ImageEditorDialog
        open={editorOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditorOpen(false);
            pendingGalleryFilesRef.current = [];
            pendingOriginalFileRef.current = null;
            editorGalleryAssetRef.current = null;
          }
        }}
        imageSrc={editorImageSrc}
        originalFileName={editorFileName}
        onSave={handleEditorSave}
        onSaveAsGallery={editorDialogMode === 'cover' ? handleEditorSaveAsGallery : undefined}
        isSaving={editorSaving}
        intent={editorDialogMode === 'cover' ? coverIntent : galleryIntent}
        mode={editorDialogMode}
      />
    </div>
  );
}
